import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import AgentTicket from '../../components/Main/AgentTicket/AgentTicket';
import AgentQueue from '../../components/Main/AgentQueue/AgentQueue';
import AgentButtonGroup from '../../components/Main/AgentButtunGroup/AgentButtonGroup';
import { AgentStatus, TicketStatus } from '../../types/enums';
import classes from './Agent1Page.module.scss';
import { ModalRedirection } from '../../components/Main/Modals/ModalRedirection/ModalRedirection';
import { useGetAgentQueue } from '../../hooks/Agents/useGetAgentQueue';
import { TicketType, useGetUsedTicket } from '../../hooks/Agents/useGetUsedTicket';
import { useGetTicketComments } from '../../hooks/Agents/useGetTicketComments';
import ModalTicketInWorking from '../../components/Main/Modals/ModalTicketInWorking/ModalTicketInWorking';
import ModalAutoDistributeWarning from '../../components/Main/Modals/ModalAutoDistributeWarning/ModalAutoDistributeWarning';
import AgentsQueueNotification from '../../components/Main/AgentNotifications/AgentNotifications';
import { isNotificationEnabled } from '../../constants/API';
import { ClientLicenseHasExpiredModal } from '../../components/LicenseModals/LicenseHasExpiredModal/ClientLicenseHasExpiredModal';
import {
  OperatorPresence,
  useOperatorPresence,
} from '../../hooks/Agents/useOperatorPresence';

export type AgentsTicketType = {
  id: number;
  name: string;
  dateTime: string;
  status?: string; // todo waiting | fullfilled
  topicName: string;
  privilegeKind?: string | null;
  priority?: number | null;
  extraData?: string | null;
};

export type TicketCommentsType = {
  map(arg0: (comment: string, index: number) => React.JSX.Element): React.ReactNode;
  comments: string[] | [];
};

export type AgentsQueueType = {
  activeItems: AgentsTicketType[];
  finishedItems: AgentsTicketType[];
};

type Agent1PageProps = {
  isWorkPlaceSelected: boolean;
};

const Agent1Page: FC<Agent1PageProps> = ({ isWorkPlaceSelected }) => {
  const workplaceTitle = (() => {
    const raw = localStorage.getItem('workplace');
    if (!raw) return null;
    try {
      return (JSON.parse(raw)?.title as string | undefined) ?? null;
    } catch {
      return null;
    }
  })();
  const [activeTicket, setActiveTicket] = useState<AgentsTicketType | null>(null);
  const [agentStatus, setAgentStatus] = useState(AgentStatus.Waiting);
  const [timeToRequest, setTimeTorequest] = useState<number | null>(null);
  const [ticketStatus, setTicketStatus] = useState(null);
  const [isAgentWorkInQueue, setIsAgentWorkInQueue] = useState(true);
  const { agentQueueResp, isSuccessAgentQueue } = useGetAgentQueue(isWorkPlaceSelected);
  const [refreshTicket, setRefreshTicket] = useState<TicketType | null>(null);
  const { usedTicket } = useGetUsedTicket(isWorkPlaceSelected, setRefreshTicket);
  const { ticketCommentsResp, isSuccessTicketComments } = useGetTicketComments(activeTicket?.id);
  const comments: TicketCommentsType = isSuccessTicketComments
    ? ticketCommentsResp?.data?.comments
    : [];
  const agentQueue = (isSuccessAgentQueue ? agentQueueResp?.data : null) as AgentsQueueType | null;
  const [queueStatus, setQueueStatus] = useState(null);
  useEffect(() => {
    agentQueue?.activeItems?.length > 0 ? setQueueStatus(true) : setQueueStatus(false);
  }, [agentQueue]);

  const [modalRedirectID, setModalRedirectID] = useState<number | null>(null);
  const [isActiveModalInWorking, setActiveModalInWorking] = useState<boolean>(false);
  const [isAutoDistributeModalActive, setAutoDistributeModalActive] = useState(false);
  const autoDistributeModalShownRef = useRef(false);
  const { presenceData, setPresence, isUpdatingPresence } =
    useOperatorPresence(isWorkPlaceSelected);
  const benefitTicketColor = presenceData?.benefitTicketColor ?? '#cfe2ff';
  const otoTicketColor = presenceData?.otoTicketColor ?? '#f8d7da';

  useEffect(() => {
    if (!isWorkPlaceSelected) {
      autoDistributeModalShownRef.current = false;
      setAutoDistributeModalActive(false);
      return;
    }
    if (presenceData?.autoDistributeEnabled && !autoDistributeModalShownRef.current) {
      autoDistributeModalShownRef.current = true;
      setAutoDistributeModalActive(true);
    }
  }, [isWorkPlaceSelected, presenceData?.autoDistributeEnabled]);

  const handleAutoDistributePresence = useCallback(
    (presence: OperatorPresence) => {
      setPresence(presence);
      setAutoDistributeModalActive(false);
    },
    [setPresence]
  );

  const handleAutoDistributeCountdownComplete = useCallback(() => {
    setPresence('FREE');
    setAutoDistributeModalActive(false);
  }, [setPresence]);

  useEffect(() => {
    if (agentStatus === AgentStatus.Requesting) {
      setTimeTorequest(Date.now());
    }
    if (agentStatus === AgentStatus.Waiting) {
      setTimeTorequest(null);
    }
  }, [agentStatus]);

  useEffect(() => {
    if (refreshTicket) {
      const { createdAt, itemId, name, topicName, requestedAt, status, extraData } = refreshTicket;
      const queueItem = agentQueue?.activeItems?.find((item) => item.id === itemId);
      setActiveTicket((prev) => ({
        id: itemId,
        name,
        dateTime: createdAt,
        topicName,
        extraData: extraData ?? queueItem?.extraData ?? prev?.extraData ?? null,
        privilegeKind: queueItem?.privilegeKind ?? prev?.privilegeKind ?? null
      }));
      const requestedMs =
        requestedAt != null ? new Date(requestedAt).getTime() : Date.now();
      setTimeTorequest(Number.isFinite(requestedMs) ? requestedMs : Date.now());
      setTicketStatus(status);
      if (status === TicketStatus.TakeInWorking) {
        setAgentStatus(AgentStatus.Working);
      }
      if (
        status === TicketStatus.CallFirstClientInQueue ||
        status === TicketStatus.CallPriorityClientinQueue ||
        status === TicketStatus.RepaetedCallClient
      ) {
        setAgentStatus(AgentStatus.Requesting);
      }
    } else if (refreshTicket == null && agentStatus === AgentStatus.Requesting) {
      setAgentStatus(AgentStatus.Waiting);
      setActiveTicket(null);
      setTimeTorequest(null);
    }
  }, [refreshTicket]);

  return (
    <div>
      {workplaceTitle && (
        <div className={classes.workplaceHeader}>
          <span className={classes.workplaceTitle}>{workplaceTitle}</span>
        </div>
      )}
      <div className={classes.container}>
        {isNotificationEnabled && (
          <AgentsQueueNotification
            isAgentWorkInQueue={isAgentWorkInQueue}
            setIsAgentWorkInQueue={setIsAgentWorkInQueue}
          />
        )}

        <div>
          <AgentQueue
            agentTickets={agentQueue?.activeItems}
            setActiveTicket={setActiveTicket}
            activeTicket={activeTicket}
            agentStatus={agentStatus}
            benefitTicketColor={benefitTicketColor}
            otoTicketColor={otoTicketColor}
          />
        </div>
        <div className={classes.ticket}>
          <div className={classes.ticketContainer}>
            {activeTicket && (
              <AgentTicket
                timeToRequest={timeToRequest}
                ticket={activeTicket}
                agentStatus={agentStatus}
                comments={comments}
                benefitTicketColor={benefitTicketColor}
                otoTicketColor={otoTicketColor}
              />
            )}
          </div>
          <AgentButtonGroup
            queueStatus={queueStatus}
            ticketStatus={ticketStatus}
            agentStatus={agentStatus}
            handleChangeAgentStatus={setAgentStatus}
            ticketId={activeTicket && activeTicket.id}
            activeItems={agentQueue?.activeItems}
            setActiveTicket={setActiveTicket}
            setModalRedirectID={setModalRedirectID}
            setActiveModalInWorking={setActiveModalInWorking}
            isActiveModalInWorking={isActiveModalInWorking}
            isWorkPlaceSelected={isWorkPlaceSelected}
            timeToRequest={timeToRequest}
          />
        </div>
        <div>
          <AgentQueue
            agentTickets={agentQueue?.finishedItems}
            benefitTicketColor={benefitTicketColor}
            otoTicketColor={otoTicketColor}
          />
        </div>
        {modalRedirectID && activeTicket && (
          <ModalRedirection
            setActive={setModalRedirectID}
            itemId={activeTicket.id}
            handleChangeAgentStatus={setAgentStatus}
            setActiveTicket={setActiveTicket}
          />
        )}
        {isActiveModalInWorking && (
          <ModalTicketInWorking
            isActive={isActiveModalInWorking}
            setActive={setActiveModalInWorking}
          />
        )}
        <ModalAutoDistributeWarning
          isActive={isAutoDistributeModalActive}
          isUpdating={isUpdatingPresence}
          onSelectPresence={handleAutoDistributePresence}
          onCountdownComplete={handleAutoDistributeCountdownComplete}
        />
      </div>

      <ClientLicenseHasExpiredModal />
    </div>
  );
};

export default Agent1Page;
